import {
  IonAlert,
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonSearchbar,
  IonSpinner,
  IonText,
  IonToggle,
  IonTitle,
  IonToolbar,
  IonNote,
  IonSelect,
  IonSelectOption,
  useIonToast,
} from "@ionic/react";
import {
  chatbubblesOutline,
  homeOutline,
  peopleOutline,
  personAddOutline,
  personCircleOutline,
  settingsOutline,
} from "ionicons/icons";
import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import Header from "src/components/Header";
import {
  agregarMiembroConversacion,
  ChatConversation,
  ChatMember,
  ChatUser,
  ChatWhitelistUser,
  ChatPerfil,
  crearConversacionGrupo,
  crearConversacionPrivada,
  listarWhitelistChat,
  listarMiembrosConversacion,
  listarConversaciones,
  listarUsuariosChat,
  obtenerPermisoChat,
  obtenerPerfilChat,
  obtenerOCrearGrupoTIC,
  quitarMiembroConversacion,
  toggleWhitelistChat,
  guardarPerfilChat,
} from "src/services/chat";

import "./Mensajes.css";

const Mensajes: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const [present] = useIonToast();

  const [loading, setLoading] = useState(true);
  const [puedeGestionar, setPuedeGestionar] = useState(false);
  const [conversaciones, setConversaciones] = useState<ChatConversation[]>([]);
  const [usuarios, setUsuarios] = useState<ChatUser[]>([]);
  const [buscarUsuario, setBuscarUsuario] = useState("");
  const [showNuevoChat, setShowNuevoChat] = useState(false);
  const [showGestionGrupo, setShowGestionGrupo] = useState(false);
  const [showCrearGrupo, setShowCrearGrupo] = useState(false);
  const [nombreGrupo, setNombreGrupo] = useState("");
  const [grupoActivo, setGrupoActivo] = useState<ChatConversation | null>(null);
  const [miembrosGrupo, setMiembrosGrupo] = useState<ChatMember[]>([]);
  const [loadingMiembros, setLoadingMiembros] = useState(false);
  const [usuarioAQuitar, setUsuarioAQuitar] = useState<ChatMember | null>(null);
  const [showWhitelist, setShowWhitelist] = useState(false);
  const [loadingWhitelist, setLoadingWhitelist] = useState(false);
  const [buscarWhitelist, setBuscarWhitelist] = useState("");
  const [usuariosWhitelist, setUsuariosWhitelist] = useState<ChatWhitelistUser[]>([]);
  const [chatHabilitado, setChatHabilitado] = useState(false);
  const [showPerfil, setShowPerfil] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [perfilChat, setPerfilChat] = useState<ChatPerfil | null>(null);
  const [nombreSeleccionado, setNombreSeleccionado] = useState("");
  const [apellidoSeleccionado, setApellidoSeleccionado] = useState("");

  const cargarPermiso = async () => {
    try {
      const permiso = await obtenerPermisoChat();
      if (!permiso?.habilitado) {
        present({
          message: "No tienes permiso para usar el chat.",
          duration: 2200,
          color: "warning",
        });
        history.replace("/home");
        return false;
      }
      setPuedeGestionar(Boolean(permiso?.puede_gestionar));
      return true;
    } catch {
      present({
        message: "No se pudieron validar permisos de chat.",
        duration: 2600,
        color: "danger",
      });
      history.replace("/home");
      return false;
    }
  };

  const cargarPerfil = async () => {
    try {
      const perfil = await obtenerPerfilChat();
      setPerfilChat(perfil || null);
      setNombreSeleccionado(String(perfil?.nombre_seleccionado || ""));
      setApellidoSeleccionado(String(perfil?.apellido_seleccionado || ""));
    } catch {
      // no bloquear chat por fallo de perfil
    }
  };

  const guardarPerfil = async () => {
    try {
      if (!nombreSeleccionado || !apellidoSeleccionado) {
        present({
          message: "Debes seleccionar un nombre y un apellido.",
          duration: 2200,
          color: "warning",
        });
        return;
      }

      await guardarPerfilChat(nombreSeleccionado, apellidoSeleccionado);
      await cargarPerfil();
      present({
        message: "Nombre visible actualizado.",
        duration: 2200,
        color: "success",
      });
      setShowPerfil(false);
      await cargarConversaciones(false);
    } catch (error: any) {
      present({
        message: error?.response?.data?.error || "No se pudo actualizar el nombre visible.",
        duration: 2800,
        color: "danger",
      });
    }
  };

  const cargarConversaciones = async (mostrarLoading: boolean = false) => {
    if (mostrarLoading) setLoading(true);
    try {
      const convData = await listarConversaciones();
      setConversaciones(convData);
    } catch (error: any) {
      present({
        message: error?.response?.data?.error || "No se pudo cargar el chat.",
        duration: 2800,
        color: "danger",
      });
    } finally {
      if (mostrarLoading) setLoading(false);
    }
  };

  const cargarUsuarios = async (q: string) => {
    try {
      const usersData = await listarUsuariosChat(q);
      setUsuarios(usersData);
    } catch (error: any) {
      present({
        message: error?.response?.data?.error || "No se pudo cargar usuarios.",
        duration: 2800,
        color: "danger",
      });
    }
  };

  useEffect(() => {
    const run = async () => {
      const ok = await cargarPermiso();
      setChatHabilitado(ok);
      if (!ok) {
        setLoading(false);
        return;
      }
      await cargarPerfil();
      await cargarConversaciones(true);
    };

    run();
  }, []);

  useEffect(() => {
    if (!chatHabilitado) return;
    const params = new URLSearchParams(location.search || "");
    const open = (params.get("open") || "").toLowerCase();
    if (!open) return;

    if (open === "perfil") {
      setShowPerfil(true);
      history.replace("/mensajes");
      return;
    }

    if (open === "config") {
      if (puedeGestionar) {
        setShowConfig(true);
      } else {
        present({
          message: "No tienes permisos para configuración.",
          duration: 2200,
          color: "warning",
        });
      }
      history.replace("/mensajes");
    }
  }, [location.search, chatHabilitado, puedeGestionar, history, present]);

  useEffect(() => {
    if (!chatHabilitado) return;
    let activo = true;

    const run = async () => {
      if (!activo) return;
      if (document.visibilityState !== "visible") return;
      await cargarConversaciones(false);
    };

    const timer = setInterval(run, 15000);

    return () => {
      activo = false;
      if (timer) clearInterval(timer);
    };
  }, [chatHabilitado]);

  useEffect(() => {
    if (!chatHabilitado) return;
    if (!showNuevoChat && !showGestionGrupo) return;

    const timer = setTimeout(() => {
      cargarUsuarios(buscarUsuario);
    }, 350);

    return () => clearTimeout(timer);
  }, [buscarUsuario, showNuevoChat, showGestionGrupo, chatHabilitado]);

  const cargarWhitelist = async () => {
    setLoadingWhitelist(true);
    try {
      const data = await listarWhitelistChat(buscarWhitelist);
      setUsuariosWhitelist(data);
    } catch (error: any) {
      present({
        message: error?.response?.data?.error || "No se pudo cargar la whitelist.",
        duration: 2800,
        color: "danger",
      });
    } finally {
      setLoadingWhitelist(false);
    }
  };

  useEffect(() => {
    if (!showWhitelist || !puedeGestionar) return;
    cargarWhitelist();
  }, [showWhitelist, buscarWhitelist, puedeGestionar]);

  const cambiarWhitelist = async (cedula: number, activo: boolean) => {
    try {
      await toggleWhitelistChat(cedula, activo);
      setUsuariosWhitelist((prev) => prev.map((u) => (
        Number(u.cedula || u.usuario) === Number(cedula)
          ? { ...u, activo_whitelist: activo }
          : u
      )));
      present({
        message: activo ? "Usuario activado en whitelist." : "Usuario desactivado de whitelist.",
        duration: 2200,
        color: "success",
      });
    } catch (error: any) {
      present({
        message: error?.response?.data?.error || "No se pudo actualizar la whitelist.",
        duration: 2800,
        color: "danger",
      });
    }
  };

  const usuariosFiltrados = useMemo(() => {
    const txt = buscarUsuario.trim().toLowerCase();
    if (!txt) return usuarios;
    return usuarios.filter((u) => {
      const label = `${u.nombre} ${u.legajo || ""} ${u.cedula || u.usuario}`.toLowerCase();
      return label.includes(txt);
    });
  }, [usuarios, buscarUsuario]);

  const formatearInformativoUltimoMensaje = (fechaTexto?: string | null) => {
    if (!fechaTexto) return "";
    const fecha = new Date(fechaTexto);
    if (Number.isNaN(fecha.getTime())) return "";

    const ahora = new Date();
    const inicioHoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
    const inicioFecha = new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate());
    const diferenciaDias = Math.round((inicioHoy.getTime() - inicioFecha.getTime()) / 86400000);

    if (diferenciaDias === 0) {
      return fecha.toLocaleTimeString("es-ES", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    }

    if (diferenciaDias === 1) {
      return "Ayer";
    }

    return fecha.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
    });
  };

  const agregarUsuarioATIC = async (usuarioDestino: number) => {
    try {
      const conversacionId = await obtenerOCrearGrupoTIC([usuarioDestino]);
      if (!conversacionId) throw new Error("No se pudo actualizar el grupo TIC");
      present({
        message: "Usuario agregado al grupo TIC.",
        duration: 2200,
        color: "success",
      });
      history.push(`/mensajes/${conversacionId}`);
    } catch (error: any) {
      present({
        message: error?.response?.data?.error || "No se pudo agregar al grupo TIC.",
        duration: 2800,
        color: "danger",
      });
    }
  };

  const abrirGrupoTIC = async () => {
    try {
      const conversacionId = await obtenerOCrearGrupoTIC();
      if (!conversacionId) throw new Error("No se pudo abrir el grupo TIC");
      history.push(`/mensajes/${conversacionId}`);
    } catch (error: any) {
      present({
        message: error?.response?.data?.error || "No se pudo abrir el grupo TIC.",
        duration: 2800,
        color: "danger",
      });
    }
  };

  const crearGrupo = async () => {
    try {
      const conversacionId = await crearConversacionGrupo(nombreGrupo, []);
      if (!conversacionId) throw new Error("No se pudo crear el grupo");
      setNombreGrupo("");
      setShowCrearGrupo(false);
      await cargarConversaciones(false);
      history.push(`/mensajes/${conversacionId}`);
    } catch (error: any) {
      present({
        message: error?.response?.data?.error || "No se pudo crear el grupo.",
        duration: 2800,
        color: "danger",
      });
    }
  };

  const cargarMiembrosGrupo = async (conversacionId: number) => {
    setLoadingMiembros(true);
    try {
      const miembros = await listarMiembrosConversacion(conversacionId);
      setMiembrosGrupo(miembros);
    } catch (error: any) {
      present({
        message: error?.response?.data?.error || "No se pudieron cargar miembros.",
        duration: 2800,
        color: "danger",
      });
    } finally {
      setLoadingMiembros(false);
    }
  };

  const abrirGestionGrupo = async (conv: ChatConversation) => {
    setGrupoActivo(conv);
    setShowGestionGrupo(true);
    if (!usuarios.length) {
      await cargarUsuarios("");
    }
    await cargarMiembrosGrupo(conv.id);
  };

  const agregarMiembro = async (usuarioDestino: number) => {
    if (!grupoActivo) return;
    try {
      await agregarMiembroConversacion(grupoActivo.id, usuarioDestino);
      present({
        message: "Miembro agregado.",
        duration: 2200,
        color: "success",
      });
      await cargarMiembrosGrupo(grupoActivo.id);
    } catch (error: any) {
      present({
        message: error?.response?.data?.error || "No se pudo agregar miembro.",
        duration: 2800,
        color: "danger",
      });
    }
  };

  const confirmarQuitarMiembro = async () => {
    if (!grupoActivo || !usuarioAQuitar) return;
    try {
      await quitarMiembroConversacion(grupoActivo.id, usuarioAQuitar.usuario);
      setUsuarioAQuitar(null);
      present({
        message: "Miembro removido.",
        duration: 2200,
        color: "success",
      });
      await cargarMiembrosGrupo(grupoActivo.id);
    } catch (error: any) {
      present({
        message: error?.response?.data?.error || "No se pudo remover miembro.",
        duration: 2800,
        color: "danger",
      });
    }
  };

  const abrirPerfil = () => {
    setShowPerfil(true);
  };

  const abrirConfig = () => {
    if (!puedeGestionar) {
      present({
        message: "No tienes permisos para configuración.",
        duration: 2200,
        color: "warning",
      });
      return;
    }
    setShowConfig(true);
  };

  const iniciarPrivado = async (usuarioDestino: number) => {
    try {
      const conversacionId = await crearConversacionPrivada(usuarioDestino);
      if (!conversacionId) throw new Error("No se pudo iniciar la conversación");
      setShowNuevoChat(false);
      history.push(`/mensajes/${conversacionId}`);
    } catch (error: any) {
      present({
        message: error?.response?.data?.error || "No se pudo crear el chat 1v1.",
        duration: 2800,
        color: "danger",
      });
    }
  };

  const miembrosIds = new Set(miembrosGrupo.map((m) => Number(m.usuario)));
  const candidatosAgregar = usuariosFiltrados.filter((u) => !miembrosIds.has(Number(u.usuario)));
  const gruposGestionables = conversaciones.filter((c) => c.tipo === "GRUPO");

  return (
    <IonPage>
      <Header
        titulo="Mensajes"
        botonesDerecha={[{ icono: homeOutline, url: "/home" }]}
      />

      <IonContent id="mensajes" className="mensajes-content" fullscreen>
        {/* acciones principales movidas a Perfil y Configuración */}

        {loading ? (
          <div className="mensajes-loading">
            <IonSpinner name="crescent" />
            <IonText>Cargando conversaciones...</IonText>
          </div>
        ) : (
          <IonList className="mensajes-lista mensajes-lista-cards">
            {conversaciones.length === 0 ? (
              <IonItem lines="none" className="mensajes-empty-item">
                <IonLabel className="ion-text-wrap">
                  <h3>No tienes conversaciones todavía</h3>
                  <p>Crea un chat 1v1 o abre el grupo TIC para iniciar el piloto.</p>
                </IonLabel>
              </IonItem>
            ) : (
              conversaciones.map((conv) => (
                <IonItem
                  key={conv.id}
                  className="mensajes-card-item"
                  lines="none"
                  button
                  detail
                  onClick={() => history.push(`/mensajes/${conv.id}`)}
                >
                  <IonLabel>
                    <div className="mensajes-row-title">
                      <h2 className="mensajes-title-main">
                        <span className="mensajes-nombre-text">{conv.nombre_mostrar || "Conversación"}</span>
                        {conv.legajo_mostrar ? (
                          <span className="mensajes-legajo-info">({String(conv.legajo_mostrar)})</span>
                        ) : null}
                      </h2>
                      <div className="mensajes-row-right">
                        <span className="mensajes-unread-slot">
                          {Number(conv.no_leidos || 0) > 0 && (
                            <span className="mensajes-unread-badge">
                              {Number(conv.no_leidos || 0) > 99 ? "99+" : Number(conv.no_leidos || 0)}
                            </span>
                          )}
                        </span>
                        <span className="mensajes-time-info">
                          {formatearInformativoUltimoMensaje(conv.ultimo_mensaje_en)}
                        </span>
                      </div>
                    </div>
                    <p className="mensajes-row-preview">
                      {conv.ultimo_mensaje || "Sin mensajes aún"}
                    </p>
                    {/* gestión de grupos movida a Configuración */}
                  </IonLabel>
                </IonItem>
              ))
            )}
          </IonList>
        )}

        <IonModal isOpen={showNuevoChat} onDidDismiss={() => setShowNuevoChat(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Nuevo chat 1v1</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowNuevoChat(false)}>Cerrar</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <IonSearchbar
              placeholder="Buscar por nombre, legajo o CI"
              value={buscarUsuario}
              onIonChange={(e) => setBuscarUsuario(String(e.detail.value || ""))}
            />
            <IonList>
              {usuariosFiltrados.map((u) => (
                <IonItem key={u.usuario}>
                  <IonLabel>
                    <h3>{u.nombre}</h3>
                    <p>Legajo: {u.legajo || "-"} | CI: {u.cedula || u.usuario}</p>
                  </IonLabel>
                  <IonButton size="small" fill="outline" onClick={() => iniciarPrivado(u.usuario)}>
                    1v1
                  </IonButton>
                  {puedeGestionar && (
                    <IonButton size="small" onClick={() => agregarUsuarioATIC(u.usuario)}>
                      + TIC
                    </IonButton>
                  )}
                </IonItem>
              ))}
              {usuariosFiltrados.length === 0 && (
                <IonItem lines="none">
                  <IonLabel>No se encontraron usuarios con ese criterio.</IonLabel>
                </IonItem>
              )}
            </IonList>
            <IonNote className="ion-padding-start ion-padding-end ion-padding-bottom">
              Puedes buscar y agregar por nombre, legajo o CI.
            </IonNote>
          </IonContent>
        </IonModal>

        <IonModal isOpen={showPerfil} onDidDismiss={() => setShowPerfil(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Nombre visible</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowPerfil(false)}>Cerrar</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <div className="ion-padding">
              <IonItem>
                <IonLabel position="stacked">Nombre actual</IonLabel>
                <IonNote>{perfilChat?.nombre_visual || perfilChat?.nombre_base || "-"}</IonNote>
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Selecciona tu nombre</IonLabel>
                <IonSelect
                  interface="alert"
                  value={nombreSeleccionado}
                  placeholder="Selecciona nombre"
                  onIonChange={(e) => setNombreSeleccionado(String(e.detail.value || ""))}
                >
                  {(perfilChat?.opciones_nombres || []).map((item) => (
                    <IonSelectOption key={`n-${item}`} value={item}>{item}</IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Selecciona tu apellido</IonLabel>
                <IonSelect
                  interface="alert"
                  value={apellidoSeleccionado}
                  placeholder="Selecciona apellido"
                  onIonChange={(e) => setApellidoSeleccionado(String(e.detail.value || ""))}
                >
                  {(perfilChat?.opciones_apellidos || []).map((item) => (
                    <IonSelectOption key={`a-${item}`} value={item}>{item}</IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
              <IonNote className="ion-padding-top ion-padding-bottom" style={{ display: "block" }}>
                Solo puedes elegir entre opciones de tu nombre real registrado.
              </IonNote>
              <IonButton expand="block" onClick={guardarPerfil} disabled={!nombreSeleccionado || !apellidoSeleccionado}>Guardar</IonButton>
            </div>
          </IonContent>
        </IonModal>

        <IonModal isOpen={showConfig} onDidDismiss={() => setShowConfig(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Configuración</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowConfig(false)}>Cerrar</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <div className="ion-padding">
              <IonButton
                expand="block"
                fill="outline"
                onClick={() => {
                  setShowConfig(false);
                  setBuscarUsuario("");
                  setShowWhitelist(true);
                }}
              >
                Gestionar whitelist
              </IonButton>

              <IonButton
                expand="block"
                fill="outline"
                className="ion-margin-top"
                onClick={() => {
                  setShowConfig(false);
                  setBuscarUsuario("");
                  setShowNuevoChat(true);
                }}
              >
                <IonIcon slot="start" icon={personAddOutline} />
                Nuevo 1v1
              </IonButton>

              <IonButton
                expand="block"
                fill="outline"
                className="ion-margin-top"
                onClick={() => {
                  setShowConfig(false);
                  setShowCrearGrupo(true);
                }}
              >
                <IonIcon slot="start" icon={peopleOutline} />
                Nuevo grupo
              </IonButton>

              <IonButton
                expand="block"
                className="ion-margin-top"
                onClick={async () => {
                  setShowConfig(false);
                  await abrirGrupoTIC();
                }}
              >
                <IonIcon slot="start" icon={peopleOutline} />
                Grupo TIC
              </IonButton>

              <IonItem lines="none" className="ion-margin-top ion-margin-bottom">
                <IonLabel>
                  <h3>Gestionar grupos</h3>
                  <p>Selecciona un grupo para administrar miembros.</p>
                </IonLabel>
              </IonItem>

              <IonList>
                {gruposGestionables.map((grupo) => (
                  <IonItem key={`cfg-grp-${grupo.id}`}>
                    <IonLabel>{grupo.nombre_mostrar || grupo.nombre || `Grupo ${grupo.id}`}</IonLabel>
                    <IonButton
                      size="small"
                      onClick={async () => {
                        setShowConfig(false);
                        await abrirGestionGrupo(grupo);
                      }}
                    >
                      Abrir
                    </IonButton>
                  </IonItem>
                ))}
                {gruposGestionables.length === 0 && (
                  <IonItem lines="none">
                    <IonLabel>No hay grupos disponibles para gestionar.</IonLabel>
                  </IonItem>
                )}
              </IonList>
            </div>
          </IonContent>
        </IonModal>

        <IonModal isOpen={showWhitelist} onDidDismiss={() => setShowWhitelist(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>WHITELIST</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowWhitelist(false)}>Cerrar</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <IonSearchbar
              placeholder="Buscar por nombre, legajo o CI"
              value={buscarWhitelist}
              onIonChange={(e) => setBuscarWhitelist(String(e.detail.value || ""))}
            />
            <IonList>
              {loadingWhitelist && (
                <IonItem lines="none">
                  <IonLabel>Cargando whitelist...</IonLabel>
                </IonItem>
              )}
              {!loadingWhitelist && usuariosWhitelist.map((u) => (
                <IonItem key={`whitelist-${u.usuario}`}>
                  <IonLabel>
                    <h3>{u.nombre}</h3>
                    <p>Legajo: {u.legajo || "-"} | CI: {u.cedula || u.usuario}</p>
                  </IonLabel>
                  <IonToggle
                    checked={Boolean(u.activo_whitelist)}
                    onIonChange={(e) => cambiarWhitelist(Number(u.cedula || u.usuario), e.detail.checked)}
                  />
                </IonItem>
              ))}
              {!loadingWhitelist && usuariosWhitelist.length === 0 && (
                <IonItem lines="none">
                  <IonLabel>No se encontraron usuarios.</IonLabel>
                </IonItem>
              )}
            </IonList>
          </IonContent>
        </IonModal>

        <IonModal isOpen={showCrearGrupo} onDidDismiss={() => setShowCrearGrupo(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Crear grupo</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowCrearGrupo(false)}>Cerrar</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <div className="ion-padding">
              <IonItem>
                <IonLabel position="stacked">Nombre del grupo</IonLabel>
                <IonSearchbar
                  value={nombreGrupo}
                  onIonChange={(e) => setNombreGrupo(String(e.detail.value || ""))}
                  placeholder="Ej: TI - Soporte"
                />
              </IonItem>
              <IonButton
                expand="block"
                onClick={crearGrupo}
                disabled={!nombreGrupo.trim()}
              >
                Crear grupo
              </IonButton>
            </div>
          </IonContent>
        </IonModal>

        <IonModal
          isOpen={showGestionGrupo}
          onDidDismiss={() => {
            setShowGestionGrupo(false);
            setGrupoActivo(null);
            setMiembrosGrupo([]);
          }}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>Panel de grupo</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowGestionGrupo(false)}>Cerrar</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <div className="ion-padding-start ion-padding-end ion-padding-top">
              <h3 className="mensajes-subtitle">{grupoActivo?.nombre_mostrar || "Grupo"}</h3>
            </div>

            <IonSearchbar
              placeholder="Buscar por nombre, legajo o CI"
              value={buscarUsuario}
              onIonChange={(e) => setBuscarUsuario(String(e.detail.value || ""))}
            />

            <IonList>
              <IonItem lines="full">
                <IonLabel>
                  <h3>Miembros</h3>
                </IonLabel>
              </IonItem>
              {loadingMiembros && (
                <IonItem lines="none">
                  <IonLabel>Cargando miembros...</IonLabel>
                </IonItem>
              )}
              {!loadingMiembros && miembrosGrupo.map((m) => (
                <IonItem key={`miembro-${m.usuario}`}>
                  <IonLabel>
                    <h3>{m.nombre}</h3>
                    <p>Legajo: {m.legajo || "-"} | CI: {m.cedula || m.usuario} | Rol: {m.rol || "MIEMBRO"}</p>
                  </IonLabel>
                  {m.rol !== "ADMIN" && (
                    <IonButton size="small" fill="outline" color="danger" onClick={() => setUsuarioAQuitar(m)}>
                      Quitar
                    </IonButton>
                  )}
                </IonItem>
              ))}

              <IonItem lines="full">
                <IonLabel>
                  <h3>Agregar miembros</h3>
                </IonLabel>
              </IonItem>
              {candidatosAgregar.map((u) => (
                <IonItem key={`candidato-${u.usuario}`}>
                  <IonLabel>
                    <h3>{u.nombre}</h3>
                    <p>Legajo: {u.legajo || "-"} | CI: {u.cedula || u.usuario}</p>
                  </IonLabel>
                  <IonButton size="small" onClick={() => agregarMiembro(u.usuario)}>
                    Agregar
                  </IonButton>
                </IonItem>
              ))}
            </IonList>
          </IonContent>
        </IonModal>

        <IonAlert
          isOpen={!!usuarioAQuitar}
          header="Quitar miembro"
          message={`¿Deseas quitar a ${usuarioAQuitar?.nombre || "este usuario"} del grupo?`}
          onDidDismiss={() => setUsuarioAQuitar(null)}
          buttons={[
            {
              text: "Cancelar",
              role: "cancel",
            },
            {
              text: "Quitar",
              role: "destructive",
              handler: confirmarQuitarMiembro,
            },
          ]}
        />
      </IonContent>

      <IonFooter className="mensajes-bottom-nav-wrap">
        <IonToolbar>
          <div className={`mensajes-bottom-nav ${puedeGestionar ? "cols-4" : "cols-3"}`}>
            <button className="mensajes-bottom-item" onClick={() => history.push("/home")}> 
              <IonIcon icon={homeOutline} />
              <span>Casa</span>
            </button>
            <button className="mensajes-bottom-item active" onClick={() => history.push("/mensajes")}> 
              <IonIcon icon={chatbubblesOutline} />
              <span>Chat</span>
            </button>
            <button className="mensajes-bottom-item" onClick={abrirPerfil}>
              <IonIcon icon={personCircleOutline} />
              <span>Perfil</span>
            </button>
            {puedeGestionar && (
              <button className="mensajes-bottom-item" onClick={abrirConfig}>
                <IonIcon icon={settingsOutline} />
                <span>Configuración</span>
              </button>
            )}
          </div>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  );
};

export default Mensajes;
